import React, { useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Menu,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  Stack,
  IconButton,
  TextField,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RenameIcon from '@mui/icons-material/DriveFileRenameOutline';
import { ConfirmDialog } from '@personal-dashboard/ui';
import { DASHBOARD_LAYOUTS, DashboardLayoutKey } from '../layouts';
import { DashboardData } from '../hooks/useDashboards';
import DashboardDialog from './DashboardDialog';

interface DashboardHeaderProps {
  dashboards: DashboardData[];
  active: DashboardData | null;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  onCreate: (name: string, layoutKey: DashboardLayoutKey) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onChangeLayout: (id: string, layoutKey: DashboardLayoutKey) => void;
  onSwitch: (id: string) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  dashboards,
  active,
  editMode,
  setEditMode,
  onCreate,
  onRename,
  onDelete,
  onChangeLayout,
  onSwitch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'rename'>('create');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const openCreate = () => {
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openRename = () => {
    setDialogMode('rename');
    setDialogOpen(true);
  };

  const handleCreate = (name: string, layoutKey: DashboardLayoutKey) => {
    onCreate(name, layoutKey);
    setDialogOpen(false);
  };

  const handleRename = (name: string) => {
    if (active) onRename(active.id, name);
    setDialogOpen(false);
  };

  const closeMenu = () => setMenuAnchor(null);

  const dashboardSwitcher = (
    <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 160 }, flexGrow: { xs: 1, sm: 0 } }}>
      <InputLabel>Dashboard</InputLabel>
      <Select
        label="Dashboard"
        value={active?.id ?? ''}
        onChange={(e) => onSwitch(e.target.value as string)}
      >
        {dashboards.map((d) => (
          <MenuItem key={d.id} value={d.id}>
            {d.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {/* Desktop header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 4, display: { xs: 'none', sm: 'flex' } }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
            Dashboard
          </Typography>
          {dashboardSwitcher}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {active && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Layout</InputLabel>
              <Select
                label="Layout"
                value={active.layoutKey}
                disabled={!editMode}
                onChange={(e) => onChangeLayout(active.id, e.target.value as DashboardLayoutKey)}
              >
                {DASHBOARD_LAYOUTS.map((layout) => (
                  <MenuItem key={layout.key} value={layout.key}>
                    {layout.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {active && (
            <TextField
              key={active.id}
              size="small"
              label="Name"
              defaultValue={active.name}
              disabled={!editMode}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                if (trimmed && trimmed !== active.name) {
                  onRename(active.id, trimmed);
                }
              }}
              sx={{ minWidth: 140 }}
            />
          )}

          <Tooltip title="Edit mode — swap graphs per slot">
            <Button
              variant={editMode ? 'contained' : 'outlined'}
              color={editMode ? 'primary' : 'inherit'}
              size="small"
              startIcon={editMode ? <CheckIcon /> : <EditIcon />}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? 'Done' : 'Edit'}
            </Button>
          </Tooltip>

          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreate}>
            New
          </Button>

          {active && (
            <Tooltip title="Rename dashboard">
              <IconButton size="small" onClick={openRename}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {active && (
            <Tooltip title="Delete dashboard">
              <IconButton size="small" color="error" onClick={() => setDeleteOpen(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Button component={Link} to="/settings" size="small" variant="outlined">
            Settings
          </Button>
        </Stack>
      </Stack>

      {/* Mobile header: switcher + options menu */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 3, display: { xs: 'flex', sm: 'none' } }}
      >
        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', flexShrink: 0 }}>
          Dashboard
        </Typography>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{dashboardSwitcher}</Box>
        <IconButton
          edge="end"
          aria-label="Dashboard options"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          size="large"
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 260, p: 1.5 } }}
      >
        <Stack spacing={1.5}>
          {active && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Layout</InputLabel>
                <Select
                  label="Layout"
                  value={active.layoutKey}
                  disabled={!editMode}
                  onChange={(e) => onChangeLayout(active.id, e.target.value as DashboardLayoutKey)}
                >
                  {DASHBOARD_LAYOUTS.map((layout) => (
                    <MenuItem key={layout.key} value={layout.key}>
                      {layout.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                key={active.id}
                size="small"
                label="Name"
                defaultValue={active.name}
                disabled={!editMode}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  if (trimmed && trimmed !== active.name) {
                    onRename(active.id, trimmed);
                  }
                }}
              />

              <Divider />
            </>
          )}

          <MenuItem onClick={() => { setEditMode(!editMode); closeMenu(); }}>
            <ListItemIcon>
              {editMode ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{editMode ? 'Done' : 'Edit'}</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => { openCreate(); closeMenu(); }}>
            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>New Dashboard</ListItemText>
          </MenuItem>

          {active && (
            <MenuItem onClick={() => { openRename(); closeMenu(); }}>
              <ListItemIcon><RenameIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
          )}

          {active && (
            <MenuItem onClick={() => { setDeleteOpen(true); closeMenu(); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}

          <MenuItem component={Link} to="/settings" onClick={closeMenu}>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
        </Stack>
      </Menu>

      <DashboardDialog
        open={dialogOpen}
        mode={dialogMode}
        initialName={active?.name ?? ''}
        onCancel={() => setDialogOpen(false)}
        onCreate={handleCreate}
        onRename={handleRename}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete dashboard?"
        message={`This will permanently delete "${active?.name ?? ''}".`}
        confirmLabel="Delete"
        severity="error"
        onConfirm={() => {
          if (active) onDelete(active.id);
          setDeleteOpen(false);
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
};
